/* eslint-disable max-len */
/**
 * @fileoverview Refined Pro Scheduler Component.
 *               PATH: src/components/contentHub/Scheduler.js
 *               ENFORCED: "No Asset, No Post" Policy.
 *               POLISHED: 21st-century UI with Auto Painter intelligence.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { 
     FiCalendar, FiLoader, FiSend, FiCheck, 
    FiArrowLeft, FiClock, FiZap, FiInfo, FiCheckCircle 
} from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { functions, db } from '../../firebaseConfig';
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { isVideoTemplate, isEphemeralTemplate } from '../shared/utils/mediaTypeUtils';
import { useConfirmDialog } from '../shared/contexts/ConfirmContext';

// API Definitions
const schedulePostsBatchFn = httpsCallable(functions, 'schedulePostsBatch');
const postContentNowFn = httpsCallable(functions, 'postContentNow', { timeout: 300000 });
const activateRecurringStreamFn = httpsCallable(functions, 'activateRecurringStream');

function getSchedulePreview(startDate, count, frequency) {
    if (!startDate || !count || !frequency) return [];
    const dates = [];
    for (let i = 0; i < count; i++) {
        const d = new Date(startDate);
        if (frequency === 'daily') d.setDate(d.getDate() + i);
        else if (frequency === 'every_second_day') d.setDate(d.getDate() + (i * 2));
        else if (frequency === 'weekly') d.setDate(d.getDate() + (i * 7));
        dates.push(d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }));
    }
    return dates;
}

function Scheduler({ 
    project, 
    posts, 
    workflowConfig, 
    contentPlanId, 
    provisionalStreamId, 
    adjustments, 
    visualSettings, 
    onSchedulingComplete,
    onBack 
}) {
    const { confirm } = useConfirmDialog();

    // --- State ---
    const [isPostingNow, setIsPostingNow] = useState(false);
    const [isScheduling, setIsScheduling] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState('');

    // Scheduling Options Toggles
    const [addAiSignature, setAddAiSignature] = useState(true); // Default ON
    const [useRandomTiming, setUseRandomTiming] = useState(true); // Default ON

    const [singlePostDate, setSinglePostDate] = useState(() => {
        const d = new Date();
        d.setHours(d.getHours() + 1, 0, 0, 0);
        return d;
    });

    const [seriesStartDate, setSeriesStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        return d;
    });
    const [seriesTime, setSeriesTime] = useState('09:00');
    const [streamName, setStreamName] = useState('');

    // --- Helpers: Auto Painter Awareness ---
    const nextAutoPainterRun = useMemo(() => {
        const now = new Date();
        const hours = [9, 13, 17, 21];
        const nextHour = hours.find(h => h > now.getUTCHours());
        const result = new Date(now);
        if (nextHour === undefined) {
            result.setUTCDate(now.getUTCDate() + 1);
            result.setUTCHours(hours[0], 0, 0, 0);
        } else {
            result.setUTCHours(nextHour, 0, 0, 0);
        }
        return result;
    }, []);

    const checkBatchReadiness = useCallback(() => {
        const required = adjustments?.platforms || workflowConfig?.platforms || ['facebook'];
        
        const result = posts.every((post, postIndex) => {
            // CRITICAL FIX: Use adjustments prop, not post.adjustments (which is undefined in workflow)
            const templates = adjustments?.templates || {};
            
            const isCarouselPost = Object.values(templates).some(t => t?.includes('carousel'));
            
            if (isCarouselPost) {
                // CAROUSEL MODE: Check visualSequence for all required platforms
                const platformResults = required.map(plat => {
                    const sequence = post.visualSequence?.[plat] || [];
                    const isReady = sequence.length >= 2 &&
                                   sequence.every(slide => slide.status === 'ready' && slide.visual?.url);

                    return isReady;
                });
                
                const allReady = platformResults.every(r => r);
                return allReady;
            } else {
                // STANDARD + VIDEO MODE: Check visual object
                const platformResults = required.map(plat => {
                    // LinkedIn supports text-only posts — no visual required
                    if (plat === 'linkedin') return true;
                    const visual = post.visual?.[plat] || (post.visual?.url && !post.visual.facebook && !post.visual.instagram && !post.visual.linkedin ? post.visual : null);
                    const hasUrl = !!visual?.url;
                    const tmpl = templates[plat] || 'standard';
                    const isVideo = isVideoTemplate(tmpl);

                    // For video templates: verify the visual is actually a video asset
                    if (isVideo && hasUrl) {
                        const isVideoAsset = visual.type === 'video' || visual.metadata?.contentType === 'video/mp4';
                        return isVideoAsset;
                    }

                    return hasUrl;
                });

                const allReady = platformResults.every(r => r);
                return allReady;
            }
        });
        
        return result;
    }, [posts, adjustments, workflowConfig]);

    const isMissingVisuals = !checkBatchReadiness();

    // Story/ephemeral detection (Phase 4: T4.8)
    const isEphemeral = useMemo(() => {
        const templates = adjustments?.templates || {};
        return Object.values(templates).some(t => isEphemeralTemplate(t));
    }, [adjustments]);

    const isScheduledTooFarForStory = useMemo(() => {
        if (!isEphemeral) return false;
        const isSingle = workflowConfig?.workflow === 'single';
        const targetDate = isSingle ? singlePostDate : seriesStartDate;
        if (!targetDate) return false;
        const hoursAhead = (targetDate.getTime() - Date.now()) / (1000 * 60 * 60);
        return hoursAhead > 48;
    }, [isEphemeral, workflowConfig, singlePostDate, seriesStartDate]);

    // --- Effects ---
    useEffect(() => {
        const isRecurringStream = workflowConfig?.workflow === 'stream' && workflowConfig?.type === 'recurring';
        if (isRecurringStream && contentPlanId) {
            const planRef = doc(db, 'contentPlans', contentPlanId);
            getDoc(planRef).then(docSnap => {
                if (docSnap.exists()) {
                    const topic = docSnap.data().plan?.refinedTopic;
                    if (topic && streamName === '') setStreamName(topic);
                    else if (streamName === '') setStreamName(project.projectName + ' Campaign');
                }
            }).catch(() => {
                if (streamName === '') setStreamName(project.projectName + ' Campaign');
            });
        } else if (streamName === '') {
             setStreamName(project.projectName + ' Campaign');
        }
    }, [contentPlanId, workflowConfig, project.projectName, streamName]);

    const isSinglePost = workflowConfig?.workflow === 'single';
    const isSeries = workflowConfig?.workflow === 'stream';
    const isRecurringStream = isSeries && workflowConfig?.type === 'recurring';
    const schedulePreview = isSeries ? getSchedulePreview(seriesStartDate, posts.length, workflowConfig.frequency) : [];

    // --- Handlers ---
    const handlePostNow = useCallback(async () => {
        if (isPostingNow || posts.length === 0 || isMissingVisuals) return;
        const confirmed = await confirm({
            title: 'Deploy Now',
            message: 'Deploy this content to your connected platforms immediately?',
            confirmLabel: 'Deploy',
            variant: 'default'
        });
        if (!confirmed) return;
        
        setIsPostingNow(true);
        setError('');
        
        try {
            await postContentNowFn({ postId: posts[0].id, projectId: project.id });
            setShowSuccess(true);
            setTimeout(() => onSchedulingComplete(), 2000);
        } catch (err) { 
            setError("Deployment failed. Please try again.");
            setIsPostingNow(false);
        }
    }, [isPostingNow, posts, project, onSchedulingComplete, isMissingVisuals, confirm]);

    const handleSchedule = useCallback(async () => {
        if (isScheduling) return;
        setIsScheduling(true);
        setError('');

        // Build scheduling options from toggles
        const schedulingOptions = {
            addAiSignature,
            useRandomTiming
        };

        try {
            if (isSinglePost) {
                const singlePostPayload = { 
                    ...posts[0], 
                    scheduledDate: singlePostDate.toISOString(), 
                    projectId: project.id,
                    adjustments: adjustments || {},
                    schedulingOptions
                };
                await schedulePostsBatchFn({ posts: [singlePostPayload] });
            } else if (isSeries) {
                if (isRecurringStream) {
                    await activateRecurringStreamFn({
                        projectId: project.id,
                        streamName: streamName,
                        contentPlanId: contentPlanId,
                        provisionalStreamId: provisionalStreamId,
                        postIds: posts.map(p => p.id),
                        frequency: workflowConfig.frequency,
                        startDate: seriesStartDate.toISOString(),
                        scheduleTime: seriesTime,
                        visualSettings: visualSettings || {},
                        adjustments: adjustments || {},
                        schedulingOptions
                    });
                } else {
                    const [hours, minutes] = seriesTime.split(':').map(Number);
                    const postsWithDates = posts.map((post, index) => {
                        const d = new Date(seriesStartDate);
                        d.setHours(hours, minutes, 0, 0);
                        if (workflowConfig.frequency === 'daily') d.setDate(d.getDate() + index);
                        else if (workflowConfig.frequency === 'every_second_day') d.setDate(d.getDate() + (index * 2));
                        else if (workflowConfig.frequency === 'weekly') d.setDate(d.getDate() + (index * 7));
                        return { ...post, scheduledDate: d.toISOString(), projectId: project.id, adjustments: adjustments || {}, schedulingOptions };
                    });
                    await schedulePostsBatchFn({ posts: postsWithDates });
                }
            }
            setShowSuccess(true);
            setTimeout(() => onSchedulingComplete(), 2000);
        } catch (err) {
            setError("Scheduling failed. Please try again.");
            setIsScheduling(false);
        }
    }, [isScheduling, posts, project, workflowConfig, singlePostDate, seriesStartDate, seriesTime, contentPlanId, onSchedulingComplete, isSinglePost, isSeries, isRecurringStream, streamName, provisionalStreamId, visualSettings, adjustments, addAiSignature, useRandomTiming]);

    // --- Render Logic ---
    if (!posts || posts.length === 0) return null;

    if (showSuccess) {
        return (
            <div className="p-20 text-center animate-fade-in bg-white/5 backdrop-blur-xl rounded-3xl border border-pastel-green/30 shadow-[0_0_50px_rgba(74,222,128,0.1)]">
                <div className="mx-auto w-24 h-24 bg-pastel-green/20 text-pastel-green rounded-full flex items-center justify-center mb-8 shadow-[0_0_30px_rgba(74,222,128,0.2)]">
                    <FiCheck size={48} />
                </div>
                <h2 className="text-4xl font-black text-white tracking-tighter mb-4">MISSION ACCOMPLISHED</h2>
                <p className="text-cream/60 text-lg font-medium">Your content has been queued for synchronization.</p>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fade-in text-left">
            
            {/* 1. Header Navigation */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                    <button 
                        onClick={onBack}
                        className="p-4 bg-white/5 border border-white/10 text-cream/40 hover:text-white rounded-2xl shadow-sm transition-all hover:bg-white/10 hover:scale-105"
                    >
                        <FiArrowLeft size={24} />
                    </button>
                    <div>
                        <h2 className="text-3xl font-black text-white tracking-tight uppercase">Release Strategy</h2>
                        <p className="text-xs text-slate-blue font-bold tracking-[0.2em] uppercase mt-2">Step 6: Launch Control</p>
                    </div>
                </div>
            </div>

            {error && <div className="p-6 bg-soft-coral/10 border-l-4 border-soft-coral text-soft-coral text-sm font-bold rounded-r-2xl shadow-[0_0_20px_rgba(244,63,94,0.1)] flex items-center gap-3"><FiInfo className="flex-shrink-0 h-5 w-5"/> {error}</div>}

            {/* 2. Automation Logic Warning */}
            {isMissingVisuals && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-3xl p-8 flex items-start gap-6 shadow-[0_0_30px_rgba(245,158,11,0.1)] backdrop-blur-sm">
                    <div className="p-4 bg-amber-500/20 rounded-2xl text-amber-400 shadow-inner">
                        <FiZap size={28}/>
                    </div>
                    <div>
                        <h4 className="text-lg font-black text-amber-200 uppercase tracking-tight">Visuals Pending Fulfillment</h4>
                        <p className="text-sm text-amber-100/70 mt-2 leading-relaxed font-medium">
                            Some posts are currently missing media. Our <b>Autonomous Painter</b> will generate these automatically during the next run at <span className="font-black text-amber-400 underline decoration-amber-500/50 underline-offset-4">{nextAutoPainterRun.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} UTC</span>.
                        </p>
                    </div>
                </div>
            )}

            {/* Story Ephemeral Warning (Phase 4: T4.8) */}
            {isEphemeral && (
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-3xl p-8 flex items-start gap-6 shadow-[0_0_30px_rgba(168,85,247,0.1)] backdrop-blur-sm">
                    <div className="p-4 bg-purple-500/20 rounded-2xl text-purple-400 shadow-inner">
                        <FiClock size={28}/>
                    </div>
                    <div>
                        <h4 className="text-lg font-black text-purple-200 uppercase tracking-tight">Story Content</h4>
                        <p className="text-sm text-purple-100/70 mt-2 leading-relaxed font-medium">
                            Stories disappear after <b>24 hours</b>. Schedule close to your target engagement window for maximum impact.
                        </p>
                        {isScheduledTooFarForStory && (
                            <p className="text-sm text-amber-300 mt-3 font-bold flex items-center gap-2">
                                <FiInfo size={16}/> Scheduled more than 48h out — consider a closer date for stories.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* SCHEDULING OPTIONS PANEL */}
            <div className="bg-gradient-to-r from-slate-blue/10 to-rosy-taupe/10 border border-slate-blue/30 rounded-3xl p-8 backdrop-blur-sm shadow-[0_0_40px_rgba(99,102,241,0.1)]">
                <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 bg-slate-blue/20 rounded-2xl text-slate-blue shadow-[0_0_20px_rgba(99,102,241,0.3)]">
                        <FiInfo size={24}/>
                    </div>
                    <div>
                        <h4 className="text-sm font-black text-white uppercase tracking-[0.2em]">Scheduling Options</h4>
                        <p className="text-xs text-cream/60 mt-1 font-medium">Configure how your posts are published.</p>
                    </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-6">
                    {/* AI Signature Toggle */}
                    <button
                        type="button"
                        onClick={() => setAddAiSignature(!addAiSignature)}
                        disabled={isScheduling || isPostingNow}
                        className={`flex-1 flex items-center justify-between p-6 rounded-3xl border transition-all duration-300 group ${
                            addAiSignature 
                                ? 'bg-slate-blue/20 border-slate-blue text-white shadow-[0_0_30px_rgba(99,102,241,0.2)]' 
                                : 'bg-white/5 border-white/10 text-cream/60 hover:bg-white/10 hover:border-white/20'
                        }`}
                    >
                        <div className="flex items-center gap-4">
                            <span className="text-2xl drop-shadow-md">🤖</span>
                            <div className="text-left">
                                <p className="text-sm font-bold tracking-wide">AI Signature</p>
                                <p className="text-[10px] opacity-60 font-medium mt-0.5">Add "Made with AI" disclosure</p>
                            </div>
                        </div>
                        <div className={`w-14 h-8 rounded-full transition-all flex items-center px-1 shadow-inner ${
                            addAiSignature ? 'bg-slate-blue justify-end' : 'bg-white/10 justify-start'
                        }`}>
                            <div className="w-6 h-6 bg-white rounded-full shadow-lg"></div>
                        </div>
                    </button>

                    {/* Random Timing Toggle */}
                    <button
                        type="button"
                        onClick={() => setUseRandomTiming(!useRandomTiming)}
                        disabled={isScheduling || isPostingNow}
                        className={`flex-1 flex items-center justify-between p-6 rounded-3xl border transition-all duration-300 group ${
                            useRandomTiming 
                                ? 'bg-slate-blue/20 border-slate-blue text-white shadow-[0_0_30px_rgba(99,102,241,0.2)]' 
                                : 'bg-white/5 border-white/10 text-cream/60 hover:bg-white/10 hover:border-white/20'
                        }`}
                    >
                        <div className="flex items-center gap-4">
                            <FiClock className={`text-2xl transition-colors ${useRandomTiming ? 'text-white' : 'text-cream/40'}`} />
                            <div className="text-left">
                                <p className="text-sm font-bold tracking-wide">Natural Timing</p>
                                <p className="text-[10px] opacity-60 font-medium mt-0.5">±1 hour random offset</p>
                            </div>
                        </div>
                        <div className={`w-14 h-8 rounded-full transition-all flex items-center px-1 shadow-inner ${
                            useRandomTiming ? 'bg-slate-blue justify-end' : 'bg-white/10 justify-start'
                        }`}>
                            <div className="w-6 h-6 bg-white rounded-full shadow-lg"></div>
                        </div>
                    </button>
                </div>
            </div>

            {/* 3. Main Action Grid */}
            <div className="grid grid-cols-1 gap-6">
                
                {/* CASE A: Single Post Actions */}
                {isSinglePost && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        
                        {/* Instant Deploy Card */}
                        <div className={`p-8 rounded-3xl border transition-all flex flex-col justify-between group ${isMissingVisuals ? 'bg-white/5 border-white/5 opacity-60 cursor-not-allowed' : 'bg-gradient-to-br from-warm-gray to-warm-black border-slate-blue/30 hover:border-slate-blue/60 shadow-lg'}`}>
                            <div>
                                <h3 className="text-xl font-black text-white uppercase tracking-tighter flex items-center gap-3">
                                    <div className="p-2 bg-slate-blue/20 rounded-lg text-slate-blue"><FiSend size={20}/></div>
                                    Deploy Now
                                </h3>
                                <p className="text-sm text-cream/60 mt-4 leading-relaxed font-medium">
                                    Send this post to your connected platforms immediately. {isMissingVisuals && "Locked until visuals are attached."}
                                </p>
                            </div>
                            <div className="mt-8">
                                <button 
                                    onClick={handlePostNow} 
                                    disabled={isPostingNow || isScheduling || isMissingVisuals} 
                                    className="w-full py-4 bg-slate-blue text-white text-xs font-black uppercase tracking-[0.2em] rounded-2xl hover:bg-slate-blue/80 shadow-[0_0_20px_rgba(99,102,241,0.4)] disabled:bg-white/10 disabled:text-cream/20 disabled:shadow-none transition-all transform active:scale-95"
                                >
                                    {isPostingNow ? <FiLoader className="animate-spin inline mr-2"/> : "Initialize Deployment"}
                                </button>
                                {isMissingVisuals && <p className="text-[10px] text-soft-coral font-bold mt-4 text-center uppercase tracking-widest italic flex items-center justify-center gap-2"><FiCheckCircle className="inline"/> Go back to Visual Studio to generate or upload all missing images before deploying</p>}
                            </div>
                        </div>

                        {/* Schedule Card */}
                        <div className="p-8 bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 shadow-lg space-y-8">
                            <h3 className="text-xl font-black text-white uppercase tracking-tighter flex items-center gap-3">
                                <div className="p-2 bg-white/10 rounded-lg text-cream"><FiCalendar size={20}/></div>
                                Schedule Post
                            </h3>
                            
                            <div>
                                <label className="block text-[10px] font-black text-cream/40 uppercase tracking-widest mb-3">Deployment Date</label>
                                <div className="relative group">
                                    <DatePicker 
                                        selected={singlePostDate} 
                                        onChange={(date) => setSinglePostDate(date)} 
                                        dateFormat="MMMM d, yyyy" 
                                        minDate={new Date()} 
                                        disabled={isPostingNow || isScheduling} 
                                        className="form-select text-sm py-3 rounded-xl bg-warm-gray/20 text-cream w-full"
                                    />
                                    <FiCalendar className="absolute right-4 top-4 text-cream/20 pointer-events-none group-hover:text-cream/40 transition-colors" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black text-cream/40 uppercase tracking-widest mb-3">Deployment Time (Local)</label>
                                <div className="flex gap-2">
                                    <select 
                                        className="form-select text-sm py-3 rounded-xl bg-warm-gray/20 text-cream w-1/2"
                                        value={singlePostDate.getHours().toString().padStart(2, '0')}
                                        onChange={(e) => {
                                            const d = new Date(singlePostDate);
                                            d.setHours(parseInt(e.target.value, 10));
                                            setSinglePostDate(d);
                                        }}
                                        disabled={isPostingNow || isScheduling}
                                    >
                                        {Array.from({ length: 24 }).map((_, i) => (
                                            <option key={i} value={i.toString().padStart(2, '0')}>{i.toString().padStart(2, '0')}</option>
                                        ))}
                                    </select>
                                    <select 
                                        className="form-select text-sm py-3 rounded-xl bg-warm-gray/20 text-cream w-1/2"
                                        value={singlePostDate.getMinutes().toString().padStart(2, '0')}
                                        onChange={(e) => {
                                            const d = new Date(singlePostDate);
                                            d.setMinutes(parseInt(e.target.value, 10));
                                            setSinglePostDate(d);
                                        }}
                                        disabled={isPostingNow || isScheduling}
                                    >
                                        {Array.from({ length: 12 }).map((_, i) => (
                                            <option key={i*5} value={(i*5).toString().padStart(2, '0')}>{(i*5).toString().padStart(2, '0')}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <button 
                                onClick={handleSchedule} 
                                disabled={isPostingNow || isScheduling} 
                                className="w-full py-4 bg-white/5 border border-white/10 text-cream text-xs font-black uppercase tracking-[0.2em] rounded-2xl hover:bg-white/10 hover:border-white/20 hover:text-white transition-all backdrop-blur-sm"
                            >
                                {isScheduling ? <FiLoader className="animate-spin inline mr-2"/> : "Confirm Schedule"}
                            </button>
                        </div>
                    </div>
                )}

                {/* CASE B: Series / Stream Actions */}
                {isSeries && (
                    <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden relative">
                         <div className="absolute top-0 right-0 w-64 h-64 bg-slate-blue/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                        <div className="p-8 space-y-8 relative z-10">
                            <div className="flex items-center gap-6">
                                <div className="p-5 bg-slate-blue text-white rounded-2xl shadow-[0_0_30px_rgba(99,102,241,0.4)]">
                                    <FiZap size={32}/>
                                </div>
                                <div>
                                    <h3 className="text-2xl font-black text-white tracking-tight uppercase">
                                        {isRecurringStream ? 'Activate Autonomous Stream' : 'Initialize Campaign'}
                                    </h3>
                                    <p className="text-sm text-cream/60 mt-1 font-medium">Configure the heartbeat for this production series.</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {isRecurringStream && (
                                    <div className="md:col-span-2">
                                        <label className="block text-[10px] font-black text-cream/40 uppercase tracking-widest mb-3">Campaign Identifier</label>
                                        <input 
                                            type="text" value={streamName} onChange={(e) => setStreamName(e.target.value)} 
                                            disabled={isScheduling} 
                                            className="w-full p-4 bg-white/5 border border-white/10 rounded-2xl text-sm font-bold text-white placeholder-white/20 focus:outline-none focus:border-slate-blue/50 focus:ring-1 focus:ring-slate-blue/50 transition-all" 
                                            placeholder="e.g., Daily Tech Insights"
                                        />
                                    </div>
                                )}
                                <div>
                                    <label className="block text-[10px] font-black text-cream/40 uppercase tracking-widest mb-3">Activation Date</label>
                                    <div className="relative group">
                                        <DatePicker 
                                            selected={seriesStartDate} onChange={(date) => setSeriesStartDate(date)} 
                                            dateFormat="MMMM d, yyyy" minDate={new Date()} disabled={isScheduling} 
                                            wrapperClassName="w-full"
                                            className="form-select text-sm py-3 rounded-xl bg-warm-gray/20 text-cream w-full"
                                        />
                                        <FiCalendar className="absolute right-4 top-4 text-cream/20 pointer-events-none group-hover:text-cream/40" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-cream/40 uppercase tracking-widest mb-3">Post Time (UTC)</label>
                                    <div className="flex gap-2">
                                        <select 
                                            className="form-select text-sm py-3 rounded-xl bg-warm-gray/20 text-cream w-1/2"
                                            value={seriesTime.split(':')[0]}
                                            onChange={(e) => setSeriesTime(`${e.target.value}:${seriesTime.split(':')[1]}`)}
                                            disabled={isScheduling}
                                        >
                                            {Array.from({ length: 24 }).map((_, i) => (
                                                <option key={i} value={i.toString().padStart(2, '0')}>{i.toString().padStart(2, '0')}</option>
                                            ))}
                                        </select>
                                        <select 
                                            className="form-select text-sm py-3 rounded-xl bg-warm-gray/20 text-cream w-1/2"
                                            value={seriesTime.split(':')[1]}
                                            onChange={(e) => setSeriesTime(`${seriesTime.split(':')[0]}:${e.target.value}`)}
                                            disabled={isScheduling}
                                        >
                                            {Array.from({ length: 12 }).map((_, i) => (
                                                <option key={i*5} value={(i*5).toString().padStart(2, '0')}>{(i*5).toString().padStart(2, '0')}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-slate-blue/5 p-6 rounded-2xl border border-slate-blue/20">
                                <h4 className="text-[10px] font-black text-slate-blue uppercase tracking-[0.2em] mb-4">Production Timeline:</h4>
                                <div className="flex flex-wrap gap-2">
                                    {schedulePreview.map((dateStr, i) => (
                                        <span key={i} className="text-[10px] font-black bg-white/5 text-cream/80 px-4 py-2 rounded-xl border border-white/10 shadow-sm uppercase tracking-tighter hover:bg-white/10 hover:text-white transition-colors cursor-default">
                                            {dateStr}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="p-8 bg-black/20 border-t border-white/10 flex justify-end backdrop-blur-md">
                            <button 
                                onClick={handleSchedule} 
                                disabled={isScheduling || (isRecurringStream && !streamName)} 
                                className="w-full md:w-auto px-12 py-5 bg-slate-blue text-white text-xs font-black uppercase tracking-[0.2em] rounded-2xl hover:bg-slate-blue/90 shadow-[0_0_30px_rgba(99,102,241,0.4)] transition-all transform active:scale-95 disabled:opacity-50 disabled:shadow-none"
                            >
                                {isScheduling ? <FiLoader className="animate-spin inline mr-2"/> : (isRecurringStream ? 'Engage Stream' : 'Deploy Series')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

Scheduler.propTypes = {
    project: PropTypes.object.isRequired,
    posts: PropTypes.arrayOf(PropTypes.object).isRequired,
    workflowConfig: PropTypes.object.isRequired,
    contentPlanId: PropTypes.string,
    provisionalStreamId: PropTypes.string,
    adjustments: PropTypes.object,
    visualSettings: PropTypes.object, 
    onSchedulingComplete: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired 
};

export default Scheduler;
